import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import {
  Instagram,
  Linkedin,
  Facebook,
  Mail,
  Phone,
  MapPin,
  ArrowUp,
} from "lucide-react";
import { footerData } from "../../../data/footer";
import "./Footer.css";

const iconMap = {
  Instagram: <Instagram size={20} strokeWidth={1.5} />,
  Linkedin: <Linkedin size={20} strokeWidth={1.5} />,
  Facebook: <Facebook size={20} strokeWidth={1.5} />,
  Mail: <Mail size={16} />,
  Phone: <Phone size={16} />,
  MapPin: <MapPin size={16} />,
};

const Footer = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleLinkClick = (e, path) => {
    // Check if it's a hash link (service section link)
    if (path.includes("#")) {
      e.preventDefault();
      const hash = path.split("#")[1];
      const basePath = path.split("#")[0];

      // If we're already on the target page
      if (location.pathname === basePath) {
        const element = document.getElementById(hash);
        if (element) {
          window.scrollTo({
            top: element.offsetTop - 90,
            behavior: "smooth",
          });
        }
      } else {
        // Navigate to the page first, then scroll
        navigate(basePath);
        setTimeout(() => {
          const element = document.getElementById(hash);
          if (element) {
            window.scrollTo({
              top: element.offsetTop - 90,
              behavior: "smooth",
            });
          }
        }, 400);
      }
    }
    // For regular internal links, let React Router handle it
    // For external links (mailto, tel, http), default behavior
  };

  return (
    <footer className="footer">
      <div className="footer__container">
        <div className="footer__top">
          <div className="footer__brand">
            <div className="footer__logo">{footerData.logo}</div>
            <p className="footer__tagline">{footerData.tagline}</p>
            <div className="footer__social">
              {footerData.social.map((social, index) => (
                <a
                  key={index}
                  href={social.url}
                  className="footer__social-link"
                  target="_blank"
                  rel="noopener noreferrer"
                  aria-label={social.name}
                >
                  {iconMap[social.iconName]}
                </a>
              ))}
            </div>
          </div>

          {footerData.sections.map((section, index) => (
            <div key={index} className="footer__section">
              <h3 className="footer__section-title">{section.title}</h3>
              <ul className="footer__links">
                {section.links.map((link, linkIndex) => (
                  <li key={linkIndex}>
                    {/* Check if it's an internal link (starts with /) */}
                    {link.path.startsWith("/") ? (
                      // Check if it has a hash (service section link)
                      link.path.includes("#") ? (
                        <a
                          href={link.path}
                          className="footer__link"
                          onClick={(e) => handleLinkClick(e, link.path)}
                        >
                          {link.iconName && (
                            <span className="footer__link-icon">
                              {iconMap[link.iconName]}
                            </span>
                          )}
                          {link.text}
                        </a>
                      ) : (
                        // Regular internal link
                        <Link to={link.path} className="footer__link">
                          {link.iconName && (
                            <span className="footer__link-icon">
                              {iconMap[link.iconName]}
                            </span>
                          )}
                          {link.text}
                        </Link>
                      )
                    ) : (
                      // External link (mailto, tel, http)

                      <a
                        href={link.path}
                        className="footer__link"
                        target={
                          link.path.startsWith("http") ? "_blank" : "_self"
                        }
                        rel={
                          link.path.startsWith("http")
                            ? "noopener noreferrer"
                            : undefined
                        }
                      >
                        {link.iconName && (
                          <span className="footer__link-icon">
                            {iconMap[link.iconName]}
                          </span>
                        )}
                        {link.text}
                      </a>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="footer__bottom">
          <p className="footer__copyright">{footerData.copyright}</p>
          <div className="footer__legal">
            {footerData.legal.map((item, index) => (
              <Link key={index} to={item.path} className="footer__legal-link">
                {item.text}
              </Link>
            ))}
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
